// ============================================================
// report-modal.js — "Tell us what's wrong" submission modal.
//
// U1 (2026-04-26) — DESIGN_LOG U1.
//
// Triggered from the topbar Report button (mountReportTopbarButton).
// Captures: text + optional photo + auto-attached page context.
// Writes via reporter.submitReport().
//
// Conversational microcopy throughout — no ERP-speak. The whole point
// is to make staff feel safe filing things ("I broke it" / "Number wrong"
// / "This wording confuses me"). Plain language, friendly tone, no
// technical fields visible to the user.
// ============================================================

import { el, showToast } from '../ui.js';
import { openModal } from './modal.js';
import { submitReport } from '../platform/reporter.js';

/**
 * Open the report modal.
 *   ctx: { user, role }   (used to auto-attach who filed it)
 */
export function openReportModal({ user, role, pageLabel } = {}) {
  let busy = false;
  let photoFile = null;

  // ── Form fields ──
  const textarea = el('textarea', {
    rows: 5,
    name: 'text',
    placeholder: 'e.g. "Stock count for RM-001 looks wrong — should be 50 not 5", or "The Apply Leave button doesn\'t do anything", or "Can we add a phone field to suppliers?"',
    style: { width: '100%', resize: 'vertical', minHeight: '120px' }
  });

  const fileInput = el('input', {
    type: 'file',
    accept: 'image/*',
    style: { display: 'none' }
  });
  const fileLabel = el('div', { class: 'muted', style: { fontSize: '12px' } },
    'No photo attached. Add one if it helps.');
  const filePicker = el('button', {
    type: 'button',
    class: 'btn btn-secondary'
  }, '📷 Add photo or screenshot');
  filePicker.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', () => {
    photoFile = fileInput.files[0] || null;
    if (photoFile) {
      if (photoFile.size > 5 * 1024 * 1024) {
        photoFile = null;
        fileInput.value = '';
        fileLabel.textContent = 'Photo too big (5 MB max). Try a smaller one.';
        fileLabel.style.color = 'var(--red)';
        return;
      }
      fileLabel.textContent = '✓ ' + photoFile.name + ' (' + (photoFile.size / 1024).toFixed(0) + ' KB)';
      fileLabel.style.color = 'var(--green)';
    } else {
      fileLabel.textContent = 'No photo attached. Add one if it helps.';
      fileLabel.style.color = '';
    }
  });

  // Auto-context preview (so user sees what we're attaching — transparency).
  const ctxPage  = pageLabel || _routeLabelFromHash();
  const ctxUser  = (user && (user.displayName || user.email)) || 'Unknown';

  const ctxPreview = el('div', { class: 'note-tip', style: { fontSize: '12px' } },
    el('div', { style: { fontWeight: 600, marginBottom: '4px' } }, 'We\'ll also attach (so we can find the issue):'),
    el('div', {}, '· Page: ', el('strong', {}, ctxPage)),
    el('div', {}, '· From: ', el('strong', {}, ctxUser), ' (', role || 'staff', ')'),
    el('div', {}, '· Time: ', el('strong', {}, new Date().toLocaleString('en-MY')))
  );

  const body = el('form', { class: 'form-stack', onsubmit: (e) => { e.preventDefault(); doSend(); } },
    el('div', { class: 'form-group' },
      el('label', {}, 'What happened?'),
      textarea,
      el('div', { class: 'muted', style: { fontSize: '11px', marginTop: '4px' } },
        'Plain language is fine. We read every report.')
    ),
    el('div', { class: 'form-group' },
      el('label', {}, 'Screenshot (optional)'),
      el('div', { style: { display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' } },
        filePicker,
        fileLabel
      ),
      fileInput
    ),
    ctxPreview
  );

  const submitBtn = el('button', { type: 'button', class: 'btn btn-primary' }, 'Send report');
  const cancelBtn = el('button', { type: 'button', class: 'btn btn-secondary' }, 'Cancel');

  const handle = openModal({
    title: 'Tell us what\'s wrong',
    body,
    footer: [cancelBtn, submitBtn],
    size: 'md'
  });
  cancelBtn.addEventListener('click', () => handle.close());
  submitBtn.addEventListener('click', () => doSend());

  // Focus the textarea so user can start typing immediately.
  setTimeout(() => textarea.focus(), 60);

  async function doSend() {
    if (busy) return;
    const text = textarea.value.trim();
    if (!text) {
      showToast('Type a few words first — we need to know what to look at.', 'error');
      textarea.focus();
      return;
    }

    busy = true;
    submitBtn.disabled = true;
    cancelBtn.disabled = true;
    submitBtn.innerHTML = '<span class="spinner"></span> Sending…';

    try {
      await submitReport({
        text,
        pageUrl:   window.location.hash || '',
        pageLabel: ctxPage,
        userName:  user && (user.displayName || user.email) || '',
        userRole:  role || '',
        photoFile
      });
      // Replace body with thank-you state — no toast (this is the moment, give it space).
      handle.setBody(el('div', { class: 'success-card' },
        el('div', { class: 'success-card-icon' }, '✓'),
        el('div', { class: 'success-card-body' },
          el('div', { class: 'success-card-message' }, 'Thanks — we got it.'),
          el('div', { class: 'success-card-sub' },
            'Your report is on its way to admin. We don\'t reply to every one, but we read every one.')
        )
      ));
      submitBtn.style.display = 'none';
      cancelBtn.textContent = 'Close';
      cancelBtn.disabled = false;
      // Auto-close after 4s.
      setTimeout(() => { try { handle.close(); } catch {} }, 4000);
    } catch (err) {
      console.error('[report submit]', err);
      showToast(err.message || 'Couldn\'t send — please try again.', 'error');
      busy = false;
      submitBtn.disabled = false;
      cancelBtn.disabled = false;
      submitBtn.textContent = 'Send report';
    }
  }
}

function _routeLabelFromHash() {
  const h = (window.location.hash || '').replace(/^#/, '').split('?')[0];
  if (!h) return 'Dashboard';
  // Pretty-ify "purchase-orders" -> "Purchase Orders"
  return h.split('-').map(w => w ? w[0].toUpperCase() + w.slice(1) : '').join(' ');
}
